<script setup lang="ts">
import type { CreativeWork } from 'schema-dts'
import { BLOG_PAGE } from '~/helpers/types'
import { isCategoryPageContent } from '~/types/api/page'
import type { Page } from '~/types/api/page'
import type { BlogPage } from '~/types/api/page/BlogPage'

definePageMeta({
  middleware: ['abort-on-special-backend-redirects', 'go-to-page-one'],
})

const route = useRoute()
const asyncDataCacheKey = computed(() => `category-list-page-content-${route.path}`)

const { data: pageContent, status, error } = await useAsyncData(asyncDataCacheKey.value, () => useWagtailApiPageGetByPath(route.path))

if (error.value) {
  throw createError({ statusCode: 404, statusMessage: error.value.message })
}

const page = ref(queryValueToNumber(route.query.page) || 1)

const pageSize = 21
const asyncDataDescendantCacheKey = computed(() => `category-list-page-descendant-${route.path}-${route.query.page}`)

const { data, error: errorDescendant } = await useAsyncData(asyncDataDescendantCacheKey.value, async () => {
  if (!pageContent?.value) {
    return
  }

  page.value = queryValueToNumber(route.query.page) || 1
  if (import.meta.client) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const { results, count } = await useApi().page.getDescendantsByPageId<BlogPage>(pageContent.value.id, {
    type: BLOG_PAGE,
    order: '-first_published_at',
    page: page.value,
    page_size: pageSize,
  })

  if (count === 0) {
    throw createError({ statusCode: 404 })
  }

  return { results, count }
}, {
  watch: [asyncDataDescendantCacheKey],
})

if (errorDescendant.value) {
  throw createError({ statusCode: 404, statusMessage: errorDescendant.value.message })
}

const useScriptSchema = ({ pageContent }: { pageContent: Ref<Page | null> }) => {
  const headScript: { script: any[] } = { script: [] }

  const microdata: CreativeWork = {
    '@type': 'CreativeWork',
    'name': pageContent.value?.title,
  }
  if (pageContent.value?.meta.first_published_at) {
    microdata.dateModified = pageContent.value?.meta.first_published_at
  }
  if (pageContent.value?.meta.last_published_at) {
    microdata.dateModified = pageContent.value?.meta.last_published_at
  }
  headScript.script.push(getJsonLDScript(microdata))
  return headScript
}

useHead({
  ...usePageHead({ pageContent, addSearchParams: ['page'] }),
  ...useScriptSchema({ pageContent }),
})
</script>

<template>
  <div>
    <LazyAppPageLoading v-if="status === 'pending'" />
    <div v-else-if="isCategoryPageContent(pageContent)">
      <LazyAppBreadcrumb
        :crumbs="pageContent.meta?.breadcrumb_data || []"
        :current-title="pageContent.title"
      />

      <LazyAppHeaderPage
        :tags="pageContent.tags"
        :image="pageContent.featured_image_rendered"
        :image-caption="pageContent.featured_image_caption"
      >
        <template #title>
          {{ pageContent.title }}
        </template>

        <!-- eslint-disable vue/no-v-html -->
        <template
          v-if="pageContent.about_html"
          #subtitle
        >
          <div
            class="prose prose-xl mx-auto italic text-gray-500"
            v-html="pageContent.about_html"
          />
        </template>
      </LazyAppHeaderPage>

      <div
        v-if="data"
        class="container relative mb-8"
      >
        <LazyAppPostCardList
          :posts="data.results"
          class="mb-12"
        />

        <div v-if="data.count > pageSize">
          <LazyYscPagination
            :page-size="pageSize"
            :count="data.count"
          />
        </div>
      </div>
    </div>
  </div>
</template>
